import { createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Popup } from "semantic-ui-react";

import { DATE_FORMATS_FNS, ReactTable } from "components";
import { DMSIDS } from "models";
import { CapabilityLabel, SeverityLabel, SourceLabel } from "modules/DMSv3/Monitoring/components/DMSv3MonitoringTable/components";
import "modules/DMSv3/Monitoring/components/DMSv3MonitoringTable/DMSv3MonitoringTable.scss";
import { handlePageSizeChange, handlePaginationChange, useDMSv3MonitoringDashboardFilters, useWarningsQuery } from "modules/DMSv3/Monitoring/hooks";
import { formatDate } from "utils/dateHelperFunctions";

import type { DMSWarning } from "models";

const columnHelper = createColumnHelper<DMSWarning>();

export const DMSv3MonitoringTable = () => {
  const { t } = useTranslation();

  const { items, nb_items, nb_pages, isFetching } = useWarningsQuery();
  const { data: filters } = useDMSv3MonitoringDashboardFilters();

  const columns = useMemo(
    () => [
      columnHelper.accessor("dealer_name", {
        id: "dealer_name",
        header: () => t("v8_dealer", "Dealer"),
        cell: ({ row: { original } }) => <Popup content={original.dealer_name} trigger={<div className="dmsv3-monitoring-ellipsis">{original.dealer_name}</div>} />
      }),
      columnHelper.accessor("dms_id", {
        id: "dms_id",
        header: () => t("v8_dms", "DMS"),
        cell: ({ row: { original } }) => {
          const dmsName = DMSIDS[original.dms_id];
          return <Popup content={dmsName} trigger={<div className="dmsv3-monitoring-ellipsis">{dmsName}</div>} />;
        }
      }),
      columnHelper.accessor("location_name", {
        id: "location_name",
        header: () => t("v8_location", "Location"),
        cell: ({ row: { original } }) => <Popup content={original.location_name} trigger={<div className="dmsv3-monitoring-ellipsis">{original.location_name}</div>} />
      }),
      columnHelper.accessor("created_on", {
        id: "created_on",
        header: () => t("v8_date", "Date"),
        cell: ({ row: { original } }) => <span className="date-no-wrap">{formatDate(original.created_on, DATE_FORMATS_FNS.dateMonthYearTime)}</span>
      }),
      columnHelper.accessor("severity", {
        id: "severity",
        header: () => t("v8_severity", "Severity"),
        cell: ({ row: { original } }) => <SeverityLabel severity={original.severity} />
      }),
      columnHelper.accessor("source", {
        id: "source",
        header: () => t("v8_source", "Source"),
        cell: ({ row: { original } }) => <SourceLabel source={original.source} />
      }),
      columnHelper.accessor("dms_capability_id", {
        id: "dms_capability_id",
        header: () => t("v8_capability", "Capability"),
        cell: ({ row: { original } }) => <CapabilityLabel dmsCapabilityId={original.dms_capability_id} />
      }),
      columnHelper.accessor("message", {
        id: "message",
        header: () => t("v8_message", "Message"),
        cell: ({ row: { original } }) => <div className="dmsv3-monitoring-message">{original.message}</div>
      })
    ],
    [t]
  );

  return (
    <div className="DMSv3MonitoringTable">
      <ReactTable
        isFetching={isFetching}
        columns={columns}
        data={items}
        pageSize={filters.page_size}
        pageCount={nb_pages}
        totalItems={nb_items}
        pagination={{ pageIndex: filters.page - 1, pageSize: filters.page_size }}
        onPaginationChange={handlePaginationChange}
        onChangePageSize={handlePageSizeChange}
      />
    </div>
  );
};
